import { memo } from 'react';

/**
 * In-progress preview for the spacing-zone polygon tool — separate
 * from `DrawingLayer2D` because the live shape is *not* a closed
 * polygon yet, so the regular zone renderer (which needs ≥3 corners +
 * a closed loop) can't represent it.
 *
 * Renders the placed corners as small dots, an open polyline through
 * them, and — when a hover position is supplied — two dashed guide
 * lines: one from the last placed corner to the cursor, and (once at
 * least 2 corners exist) one from the cursor back to the first corner
 * so the coach sees the close-hint before they double-tap.
 *
 * Coordinate mapping mirrors the rest of the 2D board:
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 */
export interface ZonePolygonOverlayProps {
  /** Committed corners in world metres, in the order they were tapped. */
  corners: ReadonlyArray<{ x: number; z: number }>;
  /** Current cursor position in world metres, or null when the pointer
   *  isn't hovering the capture surface. */
  hover: { x: number; z: number } | null;
  /** Stroke colour the in-progress zone will commit with. */
  stroke: string;
  /** Stroke width the in-progress zone will commit with — used for the
   *  guide lines so the preview matches the eventual shape's weight. */
  strokeWidth: number;
}

function svgX(p: { x: number; z: number }): number {
  return -p.z;
}
function svgY(p: { x: number; z: number }): number {
  return p.x;
}

function ZonePolygonOverlayInner({
  corners,
  hover,
  stroke,
  strokeWidth,
}: ZonePolygonOverlayProps) {
  if (corners.length === 0 && hover === null) return null;

  // Polyline through committed corners — open shape so the coach sees
  // exactly what they've drawn so far. Solid stroke matching the
  // commit colour so the in-progress shape reads as "this is yours".
  const polyline =
    corners.length >= 2
      ? corners.map((p) => `${svgX(p)},${svgY(p)}`).join(' ')
      : null;

  // Guide line from the last placed corner to the cursor. Dashed so
  // it reads as ghosted / not-yet-committed.
  const last = corners.length > 0 ? corners[corners.length - 1] : null;

  // Close-hint: dashed line from cursor back to the first corner, so
  // the coach can visualise the closing edge before double-tapping.
  // Only meaningful once ≥2 corners exist (otherwise the polygon
  // can't be closed yet anyway).
  const first = corners.length >= 2 ? corners[0] : null;

  const dashGuide = `${strokeWidth * 4} ${strokeWidth * 2.5}`;

  return (
    <g
      className="t2d-zone-preview"
      data-testid="t2d-zone-preview"
      pointerEvents="none"
    >
      {polyline && (
        <polyline
          points={polyline}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {/* Corner dots — radius scales with stroke so dense / loose lines
          both produce a readable target marker. */}
      {corners.map((p, i) => (
        <circle
          key={i}
          cx={svgX(p)}
          cy={svgY(p)}
          r={strokeWidth * 1.4}
          fill={stroke}
          data-testid={`t2d-zone-preview-corner-${i}`}
        />
      ))}
      {last && hover && (
        <line
          x1={svgX(last)}
          y1={svgY(last)}
          x2={svgX(hover)}
          y2={svgY(hover)}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={dashGuide}
          strokeLinecap="round"
          data-testid="t2d-zone-preview-guide"
        />
      )}
      {first && hover && (
        <line
          x1={svgX(hover)}
          y1={svgY(hover)}
          x2={svgX(first)}
          y2={svgY(first)}
          stroke={stroke}
          strokeWidth={strokeWidth * 0.6}
          strokeDasharray={dashGuide}
          strokeLinecap="round"
          opacity={0.6}
          data-testid="t2d-zone-preview-close-hint"
        />
      )}
    </g>
  );
}

/** Memoised — only re-renders when corners / hover / style change. */
export const ZonePolygonOverlay = memo(ZonePolygonOverlayInner);
